import { Component, Input, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { SelectionModel } from "@angular/cdk/collections";
import { FlatTreeControl } from "@angular/cdk/tree";
import { MatTreeFlatDataSource, MatTreeFlattener } from "@angular/material/tree";

export class TreeEditorNode {
  parent?: TreeEditorNode;
  children?: TreeEditorNode[];
  item: string;
  value: string;
  source: any;
  sourceType: number;
}

class FlatTreeNode {
  expandable: boolean;
  item: string;
  value: string;
  source: any;
  sourceType: number;
  level: number;
}

@Component({
  selector: "tree-editor",
  styleUrls: ["./tree-editor.component.css"],
  templateUrl: "./tree-editor.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TreeEditorComponent),
      multi: true,
    },
  ],
})
export class TreeEditorComponent {
  // #region Tree Methods

  private _transformer = (node: TreeEditorNode, level: number): FlatTreeNode => {
    return {
      expandable: node.children?.length > 0,
      item: node.item,
      value: node.value,
      source: node.source,
      sourceType: node.sourceType,
      level: level
    }
  }

  treeControl = new FlatTreeControl<FlatTreeNode>(
    node => node.level, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    this._transformer, node => node.level, node => node.expandable, node => node.children);

  hasChild = (_: number, _nodeData: FlatTreeNode) => _nodeData.expandable;
  doesHaveChild = (_nodeData: FlatTreeNode) => _nodeData.expandable;

  currentSelections = new SelectionModel<FlatTreeNode>(true /* multiple */);
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  descendantsAllSelected(node: FlatTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.length > 0 && descendants.every(child => {
      return this.currentSelections.isSelected(child);
    });
  }

  descendantsPartiallySelected(node: FlatTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.currentSelections.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  nodeIsSelected(node: FlatTreeNode): boolean {
    return this.currentSelections.isSelected(node);
  }

  toggleSelection(node: FlatTreeNode): void {
    this.currentSelections.toggle(node);
    this.setSelectionRecursive(node, this.currentSelections.isSelected(node));

    if (this.nodeIsSelected(node)) {
      var addNode = this.findNodeBySource(node.source);
      if (addNode) {
        this._value.push(addNode);
      }
    } else {
      var removeNode = this.findNodeBySource(node.source);
      if (removeNode) {
        var nodeIndex = this._value.indexOf(removeNode);
        var updatedValues: TreeEditorNode[] = [];
        for (var i = 0; i < this._value.length; i++) {
          if (i !== nodeIndex) {
            updatedValues.push(this._value[i]);
          }
        }

        this._value = updatedValues;
      }
    }
  }

  setSelectionRecursive(node: FlatTreeNode, isSelected: boolean) {
    const descendants = this.treeControl.getDescendants(node);

    if (isSelected) {
      this.currentSelections.select(...descendants);
    } else {
      this.currentSelections.deselect(...descendants);
    }

    descendants.forEach(x => this.setSelectionRecursive(x, isSelected));
  }

  findNodeBySource(source: any): TreeEditorNode | undefined {
    for (var i = 0; i < this._availableTreeEditorNodes.length; i++) {
      var res = this.recursiveFindNodeBySource(this._availableTreeEditorNodes[i], source);
      if (res) {
        return res;
      }
    }
    return undefined;
  }

  recursiveFindNodeBySource(parent: TreeEditorNode, source: any): TreeEditorNode | undefined {
    if (parent.source === source) {
      return parent;
    } else {
      for (var i = 0; i < parent.children.length; i++) {
        var res = this.recursiveFindNodeBySource(parent.children[i], source);
        if (res) {
          return res;
        }
      }
    }
    return undefined;
  }

  // #endregion

  // #region Control Value Accessor
  @Input()
  set avaialbleTreeEditorNodes(nodes: TreeEditorNode[]) {
    this._availableTreeEditorNodes = nodes;
    this.dataSource.data = this._availableTreeEditorNodes;
    this.updateSelectedNodes();
  }
  public _availableTreeEditorNodes: TreeEditorNode[] = [];

  public getSelectedValues(): TreeEditorNode[] {
    return this._value;
  }

  public setSelectedValues(selections: TreeEditorNode[]) {
    this._value = selections;
    this.updateSelectedNodes();
  }

  private _value: TreeEditorNode[] = [];

  cleanupInvalid(sourceValues: TreeEditorNode[]): TreeEditorNode[] {
    var validNodes: TreeEditorNode[] = [];
    if (sourceValues && sourceValues.length > 0) {
      sourceValues.forEach(val => {
        var matched = this.treeControl.dataNodes.filter(node => node.value === val.value);
        if (matched?.length > 0) {
          validNodes.push(val);
        }
      });
    }
    return validNodes;
  }

  updateSelectedNodes(): void {
    this._value.forEach(selectedNode => {
      const matchingNodes = this.treeControl.dataNodes.filter(node => node.value === selectedNode.value);
      if (matchingNodes?.length > 0) {
        matchingNodes.forEach(node => {
          this.currentSelections.select(node);
          this.setSelectionRecursive(node, true);
        });
      }
    });
  }

  // #endregion
}
